import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { User, Phone, Mail, Lock, Save, Eye, EyeOff, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Profile() {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const { user, login, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [emailChanged, setEmailChanged] = useState(false);
  const [originalEmail, setOriginalEmail] = useState("");
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    current_password: "",
    new_password: "",
    confirm_password: ""
  });

  const fetchProfile = useCallback(async () => {
    if (!user?.id) return;
    try {
      const response = await axios.get(`${API}/auth/profile/${user.id}`);
      setFormData(prev => ({
        ...prev,
        name: response.data.name || "",
        email: response.data.email || "",
        phone: response.data.phone || ""
      }));
      setOriginalEmail(response.data.email || "");
    } catch (error) {
      // Use local data if API fails
    }
  }, [user?.id]);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || ""
      }));
      setOriginalEmail(user.email || "");
    }
    fetchProfile();
  }, [user, fetchProfile]);

  useEffect(() => {
    setEmailChanged(formData.email.toLowerCase().trim() !== originalEmail.toLowerCase());
  }, [formData.email, originalEmail]);

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData({ ...formData, phone: formatted });
  };

  const handleSaveAll = async (e) => {
    e.preventDefault();
    
    // Validate password if changing
    const isChangingPassword = formData.new_password.length > 0;
    
    if (isChangingPassword) {
      if (!formData.current_password) {
        toast.error("Please enter your current password to change it");
        return;
      }
      if (formData.new_password !== formData.confirm_password) {
        toast.error("New passwords don't match");
        return;
      }
      if (formData.new_password.length < 6) {
        toast.error("New password must be at least 6 characters");
        return;
      }
    }

    setLoading(true);
    
    try {
      // Update profile (name, email, phone)
      const cleanedPhone = formData.phone.replace(/\D/g, "");
      await axios.put(`${API}/auth/profile/${user.id}`, {
        name: formData.name,
        email: formData.email.toLowerCase().trim(),
        phone: cleanedPhone || ""
      });
      
      // Update password if provided
      if (isChangingPassword) {
        await axios.put(`${API}/auth/change-password/${user.id}`, {
          current_password: formData.current_password,
          new_password: formData.new_password
        });
        
        // Clear password fields
        setFormData(prev => ({
          ...prev,
          current_password: "",
          new_password: "",
          confirm_password: ""
        }));
      }
      
      // Update local auth context
      login({
        ...user,
        name: formData.name,
        email: formData.email.toLowerCase().trim(),
        phone: cleanedPhone || ""
      });
      
      setOriginalEmail(formData.email.toLowerCase().trim());
      
      // If email changed, show special message and log out
      if (emailChanged) {
        toast.success("Profile updated! Please log in with your new email.");
        setTimeout(() => {
          logout();
        }, 2000);
      } else {
        toast.success("Profile updated successfully!");
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl mx-auto" data-testid="profile-page">
      {/* Header */}
      <div>
        <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
          Profile Settings
        </h1>
        <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
          Update your account information
        </p>
      </div>

      {/* Single Card with All Settings */}
      <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
        <CardHeader className={`border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
          <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
            <User className="w-5 h-5 text-blue-600" />
            Account Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleSaveAll} className="space-y-6">
            
            {/* Personal Information Section */}
            <div className="space-y-4">
              <h3 className={`font-semibold ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                Personal Information
              </h3>
              
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`pl-10 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}
                    placeholder="Your name"
                    data-testid="profile-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Phone Number (Optional)</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    className={`pl-10 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}
                    placeholder="(555) 555-5555"
                    data-testid="profile-phone-input"
                  />
                </div>
              </div>
            </div>

            <Separator className={isDark ? 'bg-slate-700' : 'bg-slate-200'} />

            {/* Login Credentials Section */}
            <div className="space-y-4">
              <h3 className={`font-semibold ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                Login Credentials
              </h3>
              
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={`pl-10 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}
                    placeholder="you@example.com"
                    data-testid="profile-email-input"
                  />
                </div>
                {emailChanged && (
                  <div className={`flex items-start gap-2 p-3 rounded-lg ${isDark ? 'bg-amber-900/30 border border-amber-700' : 'bg-amber-50 border border-amber-200'}`}>
                    <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                    <p className={`text-sm ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                      You're changing your login email. After saving, you'll be logged out and need to sign in with your <strong>new email</strong>.
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>
                  Current Password <span className={`font-normal ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>(required to change password)</span>
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type={showCurrentPassword ? "text" : "password"}
                    value={formData.current_password}
                    onChange={(e) => setFormData({ ...formData, current_password: e.target.value })}
                    className={`pl-10 pr-10 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}
                    placeholder="••••••••"
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type={showNewPassword ? "text" : "password"}
                      value={formData.new_password}
                      onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                      className={`pl-10 pr-10 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}
                      placeholder="Min 6 characters"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Confirm New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type={showNewPassword ? "text" : "password"}
                      value={formData.confirm_password}
                      onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                      className={`pl-10 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}
                      placeholder="••••••••"
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>
              </div>
              
              <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                Leave password fields empty if you don't want to change your password
              </p>
            </div>

            {/* Single Save Button */}
            <div className="pt-4">
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 text-base font-semibold"
                data-testid="save-all-btn"
              >
                <Save className="w-5 h-5 mr-2" />
                {loading ? "Saving..." : "Save All Changes"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
